import React, { Suspense, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useController } from '@rest-hooks/react';

import StripePaymentForm from './StripePaymentForm';
import EupagoPaymentForm from './EupagoPaymentForm';
import PaypalPaymentForm from './PaypalPaymentForm';
import { handleValidation } from './handleValidation';
import LoadingComponent from '../LoadingComponent';

import ShippingAddressForm from 'components/ShippingAddressForm';

import { useError } from 'utils/useErrorController';
import usePaymentIntent from 'utils/usePaymentIntent';
import shouldShowProviderTabs from 'utils/shouldShowProviderTabs';
import useStripe from 'utils/useStripe';
import useEupago from 'utils/useEupago';
import { findOrderResult } from 'utils/profile/claim/findOrderInstance';

import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { t } from 'i18n/index';

const orderPropertiesDefaultState = {
    itemPrice: '',
    quantity: '',
    percentOff: '',
};

const PaymentCollection = ({
  object,
  pricingId,
  requestedAmount,
  userPhone,
  onUserPhoneChange,
  stripeState,
  onStripeStateChange,
  orderState,
  onOrderChange,
  onStepChange,
  onContentChange,
  isPass,
  onShowTickets,
  promoCode,
  shippingAddress,
  onShippingAddressChange
}) => {

  const { setLoading, loading } = useError();
  const { fetch } = useController();

  const {selectedProvider, providers} = orderState;

  const [orderPropertiesState, setOrderPropertiesState] = useState(orderPropertiesDefaultState);

  // SELL05 — a deliverable product needs an address before any payment intent
  // exists. Until it is set, the address form takes the provider forms' place.
  // SELL20 — store purchases only: an event-page purchase (the object carries
  // its listing id) is collected at the venue and never asks for an address.
  // State lives in PaymentModal so the billing-details step can prefill from it.
  // SELL24 — the snapshot's `deliverable` can lag the server (product opened
  // before the organizer flipped it). The server is authoritative, so a 422
  // naming a missing address reveals the form rather than dead-ending.
  const requiresShipping = !!object?.deliverable && !object?.event_product_listing_id;
  const [shippingAddressRequiredByServer, setShippingAddressRequiredByServer] = useState(false);
  const awaitingShippingAddress = (requiresShipping || shippingAddressRequiredByServer) && !shippingAddress;

  const showTabs = shouldShowProviderTabs({ providers, isPass, awaitingShippingAddress });

  const handleProviderChange = (provider) => {
    const newOrderState = {selectedProvider: provider, paymentIntentId: ''};
    onOrderChange((previousValue) => ({...previousValue, ...newOrderState}));
  };

  const {
    stripe,
    handleStripeCardPaymentIntent,
  } = useStripe({
    onStripeStateChange,
    onOrderChange,
    setOrderPropertiesState
  })

  const {
    processingEupago,
    handleEupagoPaymentIntent,
    handleErrorFindingOrderResult
  } = useEupago({
    onOrderChange,
    onUserPhoneChange
  });

  const handlePaypalPaymentIntent = (id) => {
    onOrderChange((previousValue) => ({...previousValue, paymentIntentId: id}));
  };

  const {
    createPaymentIntent
  } = usePaymentIntent({
    handleValidation,
    userPhone,
    object,
    pricingId,
    requestedAmount,
    promoCode,
    handleStripeCardPaymentIntent,
    handleEupagoPaymentIntent,
    handlePaypalPaymentIntent,
    shippingAddress,
    onShippingAddressRequired: () => setShippingAddressRequiredByServer(true)
  });

  const handleEupagoFormSubmission = async (e, payment_method) => {
    e.preventDefault();
    e.stopPropagation();

    if (processingEupago) {
      setLoading(true);
      findOrderResult( processingEupago, fetch, onStepChange, handleErrorFindingOrderResult, setLoading, onContentChange, onShowTickets);
    } else {
      createPaymentIntent("eupago", payment_method);
    }
  };

  useEffect(() => {
    if (selectedProvider === "stripe" && !awaitingShippingAddress) createPaymentIntent("stripe");
  }, [selectedProvider, shippingAddress]);

  const isBizumOrganization = object?.organization?.id === 1;

  const tabLabels = {
    "stripe": t('literal:card'),
    "eupago": isBizumOrganization ? "Bizum" : "MBWay",
    "paypal": "Paypal"
  };

  const pricing = object?.pricings?.find((p) => p.id === pricingId);
  const frequency = pricing?.frequency ?? "lifetime";

  // Stripe's itemPrice comes from the intent response (post promo-code discount),
  // so the Stripe items list reflects orderPropertiesState rather than the raw pricing price.
  const stripeItems = [{
    product: { ...object, unit_price: orderPropertiesState.itemPrice },
    amount: orderPropertiesState.quantity,
    frequency,
  }];

  // Eupago and PayPal use the raw pricing price and the caller-supplied requestedAmount.
  const nonStripeItems = [{
    product: { ...object, unit_price: pricing?.price },
    amount: requestedAmount,
    frequency,
  }];

  return (
    <>
      {showTabs &&
        <Box className="tabpanel-menu slim" sx={{ borderBottom: 1, borderColor: 'var(--borderColor)' }} >
          <Tabs value={providers.indexOf(selectedProvider)}>
            { providers.map( (provider, index) => (
                <Tab
                  key={ index }
                  label={ tabLabels[provider] }
                  disabled={loading || selectedProvider === provider}
                  onClick={() => handleProviderChange(provider)}
                />
              ))
            }
          </Tabs>
        </Box>
      }

      {!selectedProvider &&
        <LoadingComponent />
      }

      {selectedProvider && awaitingShippingAddress && (
        <ShippingAddressForm onSubmit={onShippingAddressChange} />
      )}

      {/* Local boundary: the payment footer suspends on its first
          user_language fetch after page load — without this, the fallback
          unwinds to the profile body's full-page boundary and the whole page
          flashes blank while the intent is being created. */}
      <Suspense fallback={<LoadingComponent />}>
        {!awaitingShippingAddress && selectedProvider === "stripe" && (
          <StripePaymentForm
            items={ stripeItems }
            onStepChange={ onStepChange }
            onContentChange={ onContentChange }
            stripeState={ stripeState }
            onShowTickets={ onShowTickets }
            stripe={ stripe }
          />
        )}

        {!awaitingShippingAddress && selectedProvider === "paypal" && (
          <PaypalPaymentForm
            items={ nonStripeItems }
            createOrder={ () => createPaymentIntent("paypal") }
            onShowTickets={ onShowTickets }
            onStepChange={ onStepChange }
            onContentChange={ onContentChange }
          />
        )}

        {!awaitingShippingAddress && selectedProvider === "eupago" && (
          <EupagoPaymentForm
            items={ nonStripeItems }
            userPhone={ userPhone }
            onUserPhoneChange={ onUserPhoneChange }
            onSubmit={ handleEupagoFormSubmission }
            processingEupago={ processingEupago }
            redirectUrl={ orderState.redirectUrl }
            isBizumOrganization={ isBizumOrganization }
          />
        )}
      </Suspense>
    </>
  );
};

PaymentCollection.propTypes = {
  object: PropTypes.object,
  pricingId: PropTypes.number,
  requestedAmount: PropTypes.number,
  userPhone: PropTypes.string,
  selectedProvider: PropTypes.string,
  onStepChange: PropTypes.func,
  onContentChange: PropTypes.func,
  onOrderChange: PropTypes.func,
  onUserPhoneChange: PropTypes.func,
  onBack: PropTypes.func,
  stripeState: PropTypes.object,
  onStripeStateChange: PropTypes.func,
  orderState: PropTypes.object,
  isPass: PropTypes.bool,
  onShowTickets: PropTypes.func,
  promoCode: PropTypes.string,
  shippingAddress: PropTypes.object,
  onShippingAddressChange: PropTypes.func,
};

export default PaymentCollection;
